import { defineStore } from "pinia";
import Api from "src/utils/api";
import { useUserStore } from "./userStore";
import Helper from "src/utils/helper";

export const useProfileStore = defineStore("profile_store", {
  persist: true,
  state: () => ({
    emailCache: {},
    profileCache: {},
    roles: [],
    accessGroups: [],

    // Home page settings (dashboard_settings.homePage)
    homePageFavorites: [],
    homePageFavoritesLoaded: false,

    // Global app error dialog (shown in App.vue)
    appErrorDialog: {
      isVisible: false,
      headerMsg: "Oops Error Occured !!",
      errorMsg: ""
    }
  }),

  actions: {
    async fetchEmailsByDistributionGroup(groupId) {
      if (this.emailCache[groupId]) return this.emailCache[groupId];

      const emails = await Api.Profiles.getEmailsByDistributionGroup(groupId);
      if (emails?.length) this.emailCache[groupId] = emails;

      return emails || [];
    },

    async fetchProfile(id, ignoreCache = false) {
      if (this.profileCache[id] && !ignoreCache) return this.profileCache[id];

      const profile = await Api.Profiles.getProfile(id);
      if (profile) this.profileCache[id] = profile;

      if (profile?.error) {
        throw profile.error;
      }

      // If profile is null (collection doesn't exist), return a default profile
      if (!profile || !profile.data) {
        console.warn("Profile not found, returning default profile");
        return {
          data: {
            access_groups: ["GENERAL"]
          },
          error: null
        };
      }

      return profile;
    },

    async fetchDashboardSettings(id) {
      const profile = await Api.Profiles.getProfile(id);

      if (profile.data.dashboard_settings === null) {
        return {};
      } else {
        return profile.data.dashboard_settings;
      }
    },

    async updateDashboardSettings(updateData) {
      // Validate that updateData contains the required fields
      if (!updateData.id) {
        throw new Error("User ID is required in updateData.id");
      }

      if (!updateData.dashboard_settings) {
        throw new Error(
          "dashboard_settings object is required in updateData.dashboard_settings"
        );
      }

      const response = await Api.Profiles.updateProfile(updateData);

      if (response.error) {
        throw response.error;
      }

      return response;
    },

    // Shared filter management methods
    async saveDashboardFilters(dashboardKey, filters, from, userId = null) {
      try {
        // Get user ID from parameter or use current user
        const userStore = useUserStore();
        const userData = userId ? { id: userId } : userStore.userData;

        if (!userData?.id) {
          console.warn("No user ID available for saving filters");
          return;
        }

        // Get current dashboard settings
        const dashboardSettings = await this.fetchDashboardSettings(
          userData.id
        );

        // Update the dashboard settings with the new filters
        const updatedSettings = {
          ...dashboardSettings,
          [dashboardKey]: filters
        };

        // Save to database
        await this.updateDashboardSettings({
          id: userData.id,
          dashboard_settings: updatedSettings
        });
      } catch (error) {
        console.error("Error saving dashboard filters:", error);
      }
    },

    // Same as saveDashboardFilters but throws so callers can react (toast/dialog)
    async saveDashboardFiltersStrict(dashboardKey, filters, from, userId = null) {
      // Get user ID from parameter or use current user
      const userStore = useUserStore();
      const userData = userId ? { id: userId } : userStore.userData;

      if (!userData?.id) {
        throw new Error("No user ID available for saving settings");
      }

      const dashboardSettings = await this.fetchDashboardSettings(userData.id);
      const updatedSettings = {
        ...dashboardSettings,
        [dashboardKey]: filters
      };

      await this.updateDashboardSettings({
        id: userData.id,
        dashboard_settings: updatedSettings
      });
    },

    async loadDashboardFilters(dashboardKey, userId = null) {
      try {
        // Get user ID from parameter or use current user
        const userStore = useUserStore();
        const userData = userId ? { id: userId } : userStore.userData;

        if (!userData?.id) {
          console.warn("No user ID available for loading filters");
          return null;
        }

        // Get dashboard settings from database
        const dashboardSettings = await this.fetchDashboardSettings(
          userData.id
        );

        // Check if filters exist for the specified dashboard
        if (!dashboardSettings || !dashboardSettings[dashboardKey]) {
          return null;
        }

        return dashboardSettings[dashboardKey];
      } catch (error) {
        console.error("Error loading dashboard filters:", error);
        return null;
      }
    },

    async loadHomePageFavorites(force = false) {
      try {
        if (this.homePageFavoritesLoaded && !force) {
          return this.homePageFavorites;
        }

        const saved = await this.loadDashboardFilters("homePage");
        const savedFavorites = saved?.favorites;

        if (Array.isArray(savedFavorites)) {
          this.homePageFavorites = [...new Set(savedFavorites.filter(Boolean))];
        } else {
          this.homePageFavorites = [];
        }

        this.homePageFavoritesLoaded = true;
        return this.homePageFavorites;
      } catch (error) {
        console.error("Error loading home page favorites:", error);
        this.homePageFavorites = [];
        this.homePageFavoritesLoaded = true;
        return this.homePageFavorites;
      }
    },

    setHomePageFavorites(favorites) {
      this.homePageFavorites = [...new Set((favorites || []).filter(Boolean))];
      this.homePageFavoritesLoaded = true;
    },

    async persistHomePageFavorites(from = "homePage") {
      try {
        await this.saveDashboardFiltersStrict(
          "homePage",
          { favorites: this.homePageFavorites },
          from
        );
        return { ok: true };
      } catch (error) {
        return { ok: false, error };
      }
    },

    showAppErrorDialog(errorMsg, headerMsg = "Oops Error Occured !!") {
      this.appErrorDialog = {
        isVisible: true,
        headerMsg,
        errorMsg: errorMsg ?? ""
      };
    },

    hideAppErrorDialog() {
      this.appErrorDialog = {
        ...this.appErrorDialog,
        isVisible: false
      };
    },

    async getPermissions(id = Helper.getCurrentUserId()) {
      const profile = await this.fetchProfile(id);
      return profile.data.permissions;
    },

    async fetchRoles() {
      if (this.roles.length > 0) {
        return this.roles;
      }

      const rolesMap = await Api.Profiles.getRoles();
      this.roles = rolesMap || [];
      return this.roles;
    },

    async fetchAccessGroups() {
      if (this.accessGroups.length > 0) {
        return this.accessGroups;
      }

      const accessGroups = await Api.Profiles.getAccessGroups();
      this.accessGroups = accessGroups || [];
      return this.accessGroups;
    },

    async updateProfile(profile) {
      const response = await Api.Profiles.updateProfile(profile);
      if (response.error) {
        throw response.error;
      }

      if (response.data) {
        // update the profile cache
        this.profileCache[profile.id] = {
          ...this.profileCache[profile.id],
          data: {
          ...this.profileCache[profile.id].data,
            ...response.data,
            ...profile
          }
        };
      }

      return response;
    }
  }
});
